'use client';

import { useCallback, useState } from 'react';

export interface _ISticker {
  idx: string;
  src: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface _IStickerCard {
  metadataIdx: number;
  cardName: string;
  image: string;
  width: number;
  height: number;
  stickers: _ISticker[];
}

interface UseStickerCardState {
  stickerCard: _IStickerCard | null;
  selectedStickerId: string | null;
}

interface UseStickerCardProps {
  canvasSize: { width: number; height: number };
}

export default function useStickerCard({ canvasSize }: UseStickerCardProps) {
  const [state, setState] = useState<UseStickerCardState>({
    stickerCard: null,
    selectedStickerId: null,
  });

  const initStickerCard = useCallback((card: _IStickerCard) => {
    setState({ stickerCard: card, selectedStickerId: null });
  }, []);

  const selectStickerCard = useCallback((metadataIdx: number, cardName: string, image: string) => {
    setState({
      stickerCard: {
        metadataIdx,
        cardName,
        image,
        width: canvasSize.width,
        height: canvasSize.height,
        stickers: [],
      },
      selectedStickerId: null,
    });
  }, [canvasSize]);

  const addSticker = useCallback(
    (idx: string, src: string, baseWidth = 80) => {
      if (!src) return;

      const img = new window.Image();
      img.onload = () => {
        const { width: originalWidth, height: originalHeight } = img;
        const ratio = originalHeight / originalWidth;
        const width = baseWidth;
        const height = Math.round(baseWidth * ratio);

        const x = (canvasSize.width - width) / 2;
        const y = (canvasSize.height - height) / 2;

        setState((prev) => {
          if (!prev.stickerCard) return prev;
          return {
            ...prev,
            selectedStickerId: idx,
            stickerCard: {
              ...prev.stickerCard,
              stickers: [
                ...prev.stickerCard.stickers,
                { idx, src, x, y, width, height, rotation: 0 },
              ],
            },
          };
        });
      };
      img.onerror = () => {
        setState((prev) => {
          if (!prev.stickerCard) return prev;
          return {
            ...prev,
            selectedStickerId: idx,
            stickerCard: {
              ...prev.stickerCard,
              stickers: [
                ...prev.stickerCard.stickers,
                { idx, src, x: 0, y: 0, width: baseWidth, height: baseWidth, rotation: 0 },
              ],
            },
          };
        });
      };
      img.src = src;
    },
    [canvasSize],
  );

  const removeSticker = useCallback((idx: string) => {
    setState((prev) => {
      if (!prev.stickerCard) return prev;
      return {
        ...prev,
        selectedStickerId: prev.selectedStickerId === idx ? null : prev.selectedStickerId,
        stickerCard: {
          ...prev.stickerCard,
          stickers: prev.stickerCard.stickers.filter((s) => s.idx !== idx),
        },
      };
    });
  }, []);

  const updateSticker = useCallback((idx: string, updates: Partial<_ISticker>) => {
    setState((prev) => {
      if (!prev.stickerCard) return prev;
      return {
        ...prev,
        stickerCard: {
          ...prev.stickerCard,
          stickers: prev.stickerCard.stickers.map((s) => (s.idx === idx ? { ...s, ...updates } : s)),
        },
      };
    });
  }, []);

  const selectSticker = useCallback((idx: string | null) => {
    setState((prev) => ({ ...prev, selectedStickerId: idx }));
  }, []);

  return {
    stickerCard: state.stickerCard,
    selectedStickerId: state.selectedStickerId,
    initStickerCard,
    selectStickerCard,
    addSticker,
    removeSticker,
    updateSticker,
    selectSticker,
  };
}
